import { Alert, Box } from '@mui/material';
import { ColumnOrderState, createColumnHelper } from '@tanstack/react-table';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import UsersListAutocomplete from '@/components/features/UsersList/UsersListAutocomplete';
import BaseTable from '@/components/shared/Table/BaseTable';
import StyledTableToolbar from '@/components/shared/Table/features/StyledTableToolbar';
import useBaseTable from '@/components/shared/Table/hooks/useBaseTable';
import { buildApiUrl } from '@/config/runtimeConfig';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useTableNavigation } from '@/hooks/useTableNavigation';
import { DEFAULT_PAGE_SIZE, useTablePaginationState } from '@/hooks/useTablePaginationState';
import { useUsers } from '@/hooks/useUsers';
import { fetchAndDownload } from '@/shared/api/downloadFile';
import type { components } from '@/shared/api/openapi';
import { handleSnackbarError } from '@/shared/utils/errorHandler';

import GroupChips from './components/GroupChips';

type GetUsersResponse = components['schemas']['GetUsersEndpoint_GetUsersResponse'];
type UserAllocationsExportColumnResponse = components['schemas']['UserAllocationsExportColumn'];

type ColumnVisibilityState = Record<string, boolean>;

const columnHelper = createColumnHelper<GetUsersResponse>();

const COLUMN_VISIBILITY_KEY = 'usersListColumnVisibility';

const COLUMN_ORDER_KEY = 'usersListColumnOrder';

const getStoredColumnVisibility = (): ColumnVisibilityState => {
  try {
    const stored = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (err) {
    console.warn('Failed to load column visibility from localStorage:', err);
    return {};
  }
};

const getStoredColumnOrder = (): ColumnOrderState => {
  try {
    const stored = localStorage.getItem(COLUMN_ORDER_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (err) {
    console.warn('Failed to load column order from localStorage:', err);
    return [];
  }
};

function UsersList() {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();
  const snackbar = useSnackbar();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { pagination, apiPage, setPagination } = useTablePaginationState({
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const filterString = searchParams.get('emails');

  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>(getStoredColumnVisibility);
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(getStoredColumnOrder);
  const [menuAchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const scrollTopParam = searchParams.get('scrollTop');
  const locationState = location.state as { returnSearchParams?: string } | undefined;
  const returnSearchParams = locationState?.returnSearchParams;
  const { navigateToDetail, restoreScrollPosition } = useTableNavigation({
    containerRef: tableContainerRef,
  });

  const { users, isLoading, error } = useUsers(apiPage, pagination.pageSize, 'email', filterString);

  const usersList = users?.items ?? [];

  const columns = useMemo(
    () => [
      columnHelper.accessor('email', {
        header: 'Email',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('fullName', {
        header: 'Name',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => {
          const status = info.getValue();
          if (!status) return '-';
          return `${status.charAt(0).toUpperCase()}${status.slice(1).toLowerCase()}`;
        },
      }),
      columnHelper.accessor('countryCode', {
        header: 'Country Code',
        cell: (info) => {
          const countryCode = info.getValue();
          return countryCode ? (
            countryCode
          ) : (
            <Box
              component='span'
              sx={{
                display: 'inline-block',
                width: '2ch',
                textAlign: 'center',
              }}
            >
              -
            </Box>
          );
        },
      }),
      columnHelper.accessor('allocationsGroup', {
        header: 'Inventory Allocations',
        cell: (info) => {
          const groups = info.getValue();
          const tablePagination = info.table.getState().pagination;
          const absoluteIndex = tablePagination.pageIndex * tablePagination.pageSize + info.row.index;
          return <GroupChips groups={groups} index={absoluteIndex} groupType='allocation' />;
        },
      }),
      columnHelper.accessor('distributorsGroup', {
        header: 'Distributor Groups',
        cell: (info) => {
          const groups = info.getValue();
          const tablePagination = info.table.getState().pagination;
          const absoluteIndex = tablePagination.pageIndex * tablePagination.pageSize + info.row.index;
          return <GroupChips groups={groups} index={absoluteIndex} groupType='distributor' />;
        },
      }),
      columnHelper.accessor('budgetsGroup', {
        header: 'Budget Groups',
        cell: (info) => {
          const groups = info.getValue();
          const tablePagination = info.table.getState().pagination;
          const absoluteIndex = tablePagination.pageIndex * tablePagination.pageSize + info.row.index;
          return <GroupChips groups={groups} index={absoluteIndex} groupType='budget' />;
        },
      }),
    ],
    [],
  );

  useEffect(() => {
    if (!isLoading && usersList.length > 0) {
      restoreScrollPosition();
    }
  }, [isLoading, usersList.length, restoreScrollPosition, scrollTopParam, returnSearchParams]);

  useEffect(() => {
    try {
      localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(columnVisibility));
    } catch (err) {
      console.warn('Failed to save column visibility to localStorage:', err);
    }
  }, [columnVisibility]);

  useEffect(() => {
    try {
      localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(columnOrder));
    } catch (err) {
      console.warn('Failed to save column order to localStorage:', err);
    }
  }, [columnOrder]);

  const handleResetColumnOrder = () => {
    tableWithData.table.resetColumnOrder();
    localStorage.removeItem(COLUMN_ORDER_KEY);
  };

  const isDefaultColumnOrder = (): boolean => {
    if (columns.length === 0) return true;

    if (columnOrder.length === 0) return true;

    const defaultIds = columns.map((col) => col.accessorKey);

    if (defaultIds.length !== columnOrder.length) return false;

    return defaultIds.every((id, index) => id === columnOrder[index]);
  };

  const isResetOrderDisabled = isDefaultColumnOrder();

  // Restore URL params from navigation state when coming back from detail page
  useEffect(() => {
    if (returnSearchParams) {
      const restoredParams = new URLSearchParams(returnSearchParams);
      const currentGroups = searchParams.get('emails');
      const restoredGroups = restoredParams.get('emails');

      if (restoredGroups && restoredGroups !== currentGroups) {
        setSearchParams(restoredParams, { replace: true });
      }
    }
  }, [returnSearchParams, searchParams, setSearchParams]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    if (setPagination) {
      setPagination((prev) => ({ ...prev, pageIndex: newPage }));
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);

    if (setPagination) {
      setPagination({ pageIndex: 0, pageSize: newPageSize });
    }

    const params = new URLSearchParams(window.location.search);
    params.delete('scrollTop');
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  };

  const handleRowClick = (user: GetUsersResponse) => {
    if (user.id) {
      navigateToDetail(`/users/${user.id}`, {
        user,
      });
    }
  };

  const tableWithData = useBaseTable<GetUsersResponse>({
    tableData: usersList,
    columns,
    isRowSelectionEnabled: false,
    isColumnVisibilityEnabled: true,
    isColumnOrderEnabled: true,
    columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    columnOrder,
    onColumnOrderChange: setColumnOrder,
    hasPagination: true,
    pagination,
    onPaginationChange: setPagination,
    paginationCount: users?.totalCount ?? 0,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [10, 50, 100],
  });

  const handleExport = async () => {
    if (!organizationId) {
      console.error('No organizationId available for export');
      return;
    }

    setIsExportLoading(true);

    try {
      const columnIdsToExportColumn: Partial<Record<keyof GetUsersResponse, UserAllocationsExportColumnResponse>> = {
        email: 'Email',
        fullName: 'FullName',
        countryCode: 'CountryCode',
        status: 'Status',
        allocationsGroup: 'Allocations',
        distributorsGroup: 'Distributors',
        budgetsGroup: 'Budgets',
      };

      const visibleColumns: UserAllocationsExportColumnResponse[] = Object.entries(columnIdsToExportColumn)
        .filter(([columnId]) => columnVisibility[columnId] !== false)
        .map(([, exportColumn]) => exportColumn);

      const sortedVisibleColumns =
        columnOrder.length > 0
          ? columnOrder
              .map((columnId) => columnIdsToExportColumn[columnId as keyof GetUsersResponse])
              .filter((col): col is UserAllocationsExportColumnResponse => col !== undefined && visibleColumns.includes(col))
          : visibleColumns;

      const queryParams = new URLSearchParams();

      if (filterString) {
        queryParams.set('filter', filterString);
      }

      sortedVisibleColumns.forEach((col) => queryParams.append('columns', col));

      const basePath = `/api/organizations/${encodeURIComponent(organizationId)}/users/export-users-allocations`;
      const queryString = queryParams.toString();
      const url = buildApiUrl(queryString ? `${basePath}?${queryString}` : basePath);

      await fetchAndDownload({
        url,
        getToken: getAccessToken,
        fileName: 'users.xlsx',
        accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        allowedContentTypes: ['spreadsheetml', 'openxmlformats'],
      });
    } catch (err) {
      console.error('Export error:', err);
      handleSnackbarError(err, snackbar);
    } finally {
      setIsExportLoading(false);
    }
  };

  /* Column visibility feature */
  const openMenu = Boolean(menuAchorEl);
  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(e.currentTarget);
  };
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  if (error) {
    return (
      <Alert
        severity='error'
        sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', lineHeight: '20px' }}
        data-testid='error-loading-users'
      >
        Error loading users: {error.message}
      </Alert>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <UsersListAutocomplete key={filterString ?? ''} organizationId={organizationId} />

        <StyledTableToolbar
          isColumnVisibilityEnabled
          isDndEnabled
          onColumnsOrderReset={handleResetColumnOrder}
          isResetDisabled={isResetOrderDisabled}
          menuAnchorEl={menuAchorEl}
          tableComponent={tableWithData.table}
          isMenuOpen={openMenu}
          onOpenMenu={handleOpenMenu}
          onCloseMenu={handleCloseMenu}
          alwaysVisibleColumns={[tableWithData.table.getColumn('email')!]}
          exportFeature={{
            isExportLoading: isExportLoading,
            onExport: handleExport,
          }}
        />
      </Box>

      <BaseTable<GetUsersResponse>
        {...tableWithData}
        testId='users-table'
        ariaLabel='Users list'
        containerRef={tableContainerRef}
        lockFirstColumn
        noDataMessage='No users found.'
        onRowClick={(_, row) => handleRowClick(row.original)}
        loadingData={{
          isLoading: isLoading,
          loadingMessage: 'Loading Users…',
        }}
      />
    </>
  );
}

export default UsersList;
